/**
 * IconButton component with Dashboard3 styling
 *
 * @example
 * ```tsx
 * <IconButton aria-label="Close" icon={<CloseIcon />} />
 * ```
 */

"use client";

import {
  IconButton as ChakraIconButton,
  type IconButtonProps as ChakraIconButtonProps,
} from "@chakra-ui/react";
import { forwardRef } from "react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";

export interface IconButtonProps extends ChakraIconButtonProps {
  /**
   * Button variant
   * - primary: Accent primary color
   * - muted: Accent muted color
   * - ghost: Transparent button
   * - danger: Trash/delete actions — red hover
   */
  variant?: "primary" | "muted" | "ghost" | "danger";
  /** When rendering as a Next.js link (`as={NextLink}`). */
  href?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant = "ghost", ...props }, ref) => {
    const theme = useThemeTokens();
    const interactive = useInteractiveTokens();

    const variantStyles = {
      primary: {
        bg: theme.accent.primaryBg,
        color: theme.accent.primary,
        sx: {
          "@media (hover: hover)": {
            "&:hover": {
              bg: interactive.iconButton.primary.hover,
            },
          },
          "@media (hover: none) and (pointer: coarse)": {
            "&:active": {
              bg: interactive.iconButton.primary.active,
            },
          },
        },
        _focus: {
          boxShadow: theme.focusRing,
        },
      },
      muted: {
        bg: theme.accent.mutedBg,
        color: theme.accent.muted,
        sx: {
          "@media (hover: hover)": {
            "&:hover": {
              bg: interactive.iconButton.muted.hover,
            },
          },
          "@media (hover: none) and (pointer: coarse)": {
            "&:active": {
              bg: interactive.iconButton.muted.active,
            },
          },
        },
        _focus: {
          boxShadow: theme.focusRing,
        },
      },
      ghost: {
        bg: "transparent",
        color: theme.text.secondary,
        sx: {
          "@media (hover: hover)": {
            "&:hover": {
              bg: interactive.iconButton.ghost.hover,
              color: theme.text.primary,
            },
          },
        },
        _focus: {
          boxShadow: theme.focusRing,
        },
      },
      danger: {
        bg: "transparent",
        color: theme.text.tertiary,
        sx: {
          "@media (hover: hover)": {
            "&:hover": {
              bg: interactive.iconButton.ghost.hover,
              color: "red.400",
            },
          },
        },
        _focus: {
          boxShadow: theme.focusRing,
        },
      },
    };

    return (
      <ChakraIconButton
        ref={ref}
        borderRadius="8px"
        transition="all 0.18s ease-out"
        {...variantStyles[variant]}
        {...props}
      />
    );
  },
);

IconButton.displayName = "IconButton";
